import { ChangeDetectionStrategy, Component, ElementRef, Injector, ViewChild, afterNextRender, inject } from '@angular/core'
import { AsyncPipe } from '@angular/common'
import { ActivatedRoute } from '@angular/router'
import { combineLatest, filter, map, shareReplay } from 'rxjs'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { MatIcon } from '@angular/material/icon'
import {
  ACTIVITY_LOG_AUTOMATIC_PERSON,
  ACTIVITY_LOG_SUPPORT_PERSON,
  ActivityLogCoverage,
  ActivityLogDetails,
  ActivityLogEntry,
  ActivityLogFieldChange,
  MetricsService,
} from '../metrics.service'
import { AppTableColumn, AppTableSource, TableComponent } from '../table/table.component'
import { BritishDatePipe } from '../british-date.pipe'
import { DATE_TIME_FORMAT } from '../utils'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { ClientsService } from '../clients.service'
import { queryKeys } from '../query-keys'

const DEFAULT_PAGE_SIZE = 25

const DATE_ONLY_FORMAT = 'd MMM yyyy'

/** The query parameter the table writes the Action column's filter to, which is what the banner asks about. */
const ACTION_FILTER_PARAM = 'filter.metric'

/** One row of the recording-date list: an action as the column words it, and when it began. */
type CoverageRow = { action: string; recordedSince: string }

/** An ISO instant as the backend writes one, which is what the details carry their dates as. */
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/

// The metric names are mirrored by hand in the translations and the list grows over time, so an
// action without a translation key must still render readably rather than leaking the raw key.
function humanizeToken(token: string): string {
  return token
    .split('_')
    .map((word, index) => (index === 0 ? word.charAt(0) + word.slice(1).toLowerCase() : word.toLowerCase()))
    .join(' ')
}

function humanizeFieldName(field: string): string {
  const spaced = field.replace(/([A-Z])/g, ' $1')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

@Component({
  selector: 'app-activity-log',
  standalone: true,
  imports: [AsyncPipe, TranslatePipe, MatIcon, TableComponent, ToolbarComponent],
  providers: [BritishDatePipe],
  templateUrl: './activity-log.component.html',
  styleUrl: './activity-log.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ActivityLogComponent {
  private metricsService = inject(MetricsService)
  private translateService = inject(TranslateService)
  private britishDatePipe = inject(BritishDatePipe)
  private clientsService = inject(ClientsService)
  private route = inject(ActivatedRoute)
  private injector = inject(Injector)

  readonly defaultPageSize = DEFAULT_PAGE_SIZE

  /** The newest entry first, which is what someone opening an audit log is looking for. */
  readonly defaultSort = { columnId: 'timestamp', direction: 'desc' as const }

  isCoverageOpen = false

  /** Set when the list was opened from the banner, which asks about particular actions rather than all. */
  isCoverageNarrowed = false

  @ViewChild('coverage') private coverageSection: ElementRef<HTMLElement> | null = null

  /**
   * No organization selection: the log covers the caller's whole client, and the Organisation column
   * is what narrows it. Keyed on the resource so the facet option lists share the entry.
   */
  readonly source: AppTableSource<ActivityLogEntry> = {
    url: '/api/metrics/activity-log',
    queryKey: queryKeys.metrics.activityLog(),
  }

  private organizations$ = this.clientsService.getCurrentUserClient().pipe(
    filter((result) => result.isSuccess),
    map((result) => result.data?.organizations ?? []),
    shareReplay(1)
  )

  private coverage$ = this.metricsService.getActivityLogCoverage().pipe(
    map((result) => result.data ?? null),
    shareReplay(1)
  )

  /** What the Action column is filtered to, which is what the banner has to answer for. */
  private selectedActions$ = this.route.queryParamMap.pipe(map((params) => params.getAll(ACTION_FILTER_PARAM).filter((value) => value !== '')))

  view$ = combineLatest([this.organizations$, this.coverage$, this.selectedActions$]).pipe(
    map(([organizations, coverage, selectedActions]) => {
      const gaps = this.buildGaps(coverage, selectedActions)
      return {
        // One organization means the column only ever says the same thing.
        columns: organizations.length > 1 ? this.columns : this.columns.filter((column) => column.id !== 'organizationName'),
        loggingSince: coverage === null ? null : this.formatDate(coverage.loggingSince),
        coverageRows: coverage === null ? [] : this.buildCoverageRows(coverage.actions),
        gaps,
        gapMessageKey: gaps.length === 1 ? 'ACTIVITY_LOG.COVERAGE.GAP_ONE' : 'ACTIVITY_LOG.COVERAGE.GAP_OTHER',
      }
    })
  )

  readonly columns: AppTableColumn<ActivityLogEntry>[] = [
    {
      id: 'timestamp',
      header: 'COMMON.DATE',
      value: (entry) => new Date(entry.timestamp).getTime(),
      display: (entry) => this.britishDatePipe.transform(entry.timestamp, DATE_TIME_FORMAT),
      sortable: true,
      filter: 'date',
      autoFit: true,
    },
    {
      id: 'person',
      header: 'ACTIVITY_LOG.PERSON',
      value: (entry) => this.personName(entry),
      tone: 'strong',
      filter: 'facet',
      facetLabel: (value) => this.personLabel(String(value)),
    },
    { id: 'organizationName', header: 'COMMON.ORGANIZATION', value: (entry) => entry.organization?.name ?? '', filter: 'facet', autoFit: true },
    {
      id: 'metric',
      header: 'ACTIVITY_LOG.ACTION',
      value: (entry) => this.translateAction(entry.metric),
      sortable: true,
      filter: 'facet',
      facetLabel: (value) => this.translateAction(String(value)),
    },
    {
      id: 'callId',
      header: 'ACTIVITY_LOG.MEETING_ID',
      value: (entry) => entry.call?.id ?? '',
      numeric: true,
      tone: 'muted',
      filter: 'text',
    },
    { id: 'call', header: 'COMMON.CALL', value: (entry) => entry.call?.name ?? '', maskClass: 'mask-call-name' },
    // A resource entry is described by its file name.
    { id: 'details', header: 'ACTIVITY_LOG.DETAILS', value: (entry) => this.describeDetails(entry.details), tone: 'muted', maskClass: 'mask-file-names' },
  ]

  rowId = (entry: ActivityLogEntry): string => String(entry.id)

  translateAction(metric: string): string {
    return this.translateOrHumanize(`ACTIVITY_LOG.ACTIONS.${metric}`, () => humanizeToken(metric))
  }

  private personName(entry: ActivityLogEntry): string {
    if (entry.performedAutomatically) {
      return this.translateService.instant('ACTIVITY_LOG.AUTOMATIC')
    }
    if (entry.performedByBriefAiSupport) {
      return this.translateService.instant('ACTIVITY_LOG.BRIEF_AI_SUPPORT')
    }
    // Somebody of theirs whose account has since gone. Named as that rather than left blank, which
    // would read as an entry we failed to attribute.
    return entry.user?.name ?? this.translateService.instant('ACTIVITY_LOG.DELETED_PERSON')
  }

  /** The picker's options are the names themselves, plus the two actors that are not a person. */
  private personLabel(value: string): string {
    if (value === ACTIVITY_LOG_AUTOMATIC_PERSON) {
      return this.translateService.instant('ACTIVITY_LOG.AUTOMATIC')
    }
    if (value === ACTIVITY_LOG_SUPPORT_PERSON) {
      return this.translateService.instant('ACTIVITY_LOG.BRIEF_AI_SUPPORT')
    }
    return value
  }

  private describeDetails(details: ActivityLogDetails | null): string {
    if (details === null) {
      return ''
    }
    switch (details.kind) {
      case 'SETTINGS':
        return this.describeChanges(details.changes)
      case 'USER': {
        const changes = this.describeChanges(details.changes)
        return [details.name, changes].filter((part) => part !== null && part !== '').join(' — ')
      }
      case 'RESOURCE':
        return details.name ?? this.resourceTypeLabel(details.resource)
      case 'FILE_FORMAT':
        return details.format
      case 'ACCESS': {
        const accessLevel = details.accessLevel === null ? null : this.accessLevelLabel(details.accessLevel)
        return [details.name, accessLevel].filter((part) => part !== null).join(' — ')
      }
      case 'REPROCESSING': {
        const scope = this.translateService.instant(details.scope === 'FULL' ? 'ACTIVITY_LOG.REPROCESSING_FULL' : 'ACTIVITY_LOG.REPROCESSING_PARTIAL')
        // What a full run regenerated is named the way the product names it; a scoped run is named
        // by the agenda items it covered, which arrive already worded.
        const subjects = details.scope === 'FULL' ? details.outputs.map((output) => this.outputLabel(output)) : details.items
        return [scope, subjects.join(', ')].filter((part) => part !== '').join(' — ')
      }
      case 'RETENTION':
        return this.translateService.instant('ACTIVITY_LOG.RETENTION_CHANGE', {
          from: details.from === null ? this.translateService.instant('ACTIVITY_LOG.VALUE_NONE') : this.formatDate(details.from),
          to: this.formatDate(details.to),
        })
    }
  }

  private describeChanges(changes: ActivityLogFieldChange[]): string {
    return changes.map((change) => `${this.fieldLabel(change.field)}: ${this.formatSettingValue(change.value)}`).join('; ')
  }

  private fieldLabel(field: string): string {
    return this.translateOrHumanize(`ACTIVITY_LOG.FIELDS.${field}`, () => humanizeFieldName(field))
  }

  private resourceTypeLabel(resource: string): string {
    return this.translateOrHumanize(`ACTIVITY_LOG.RESOURCES.${resource}`, () => humanizeToken(resource))
  }

  private outputLabel(output: string): string {
    return this.translateOrHumanize(`ACTIVITY_LOG.OUTPUTS.${output}`, () => humanizeToken(output))
  }

  /**
   * Worded from the roles the meeting-access dialog offers rather than from the share dialog's three:
   * an administrator may also make somebody the owner, which the share dialog has no word for.
   */
  private accessLevelLabel(accessLevel: string): string {
    return this.translateOrHumanize(`CALLS.ADMIN.MANAGE_CALL_USERS.ROLES.${accessLevel}`, () => humanizeToken(accessLevel))
  }

  private translateOrHumanize(translationKey: string, fallback: () => string): string {
    const translated = this.translateService.instant(translationKey)
    return translated === translationKey ? fallback() : translated
  }

  private formatSettingValue(value: unknown): string {
    if (typeof value === 'boolean') {
      return this.translateService.instant(value ? 'ACTIVITY_LOG.VALUE_ON' : 'ACTIVITY_LOG.VALUE_OFF')
    }
    if (value === null || value === undefined || value === '') {
      return this.translateService.instant('ACTIVITY_LOG.VALUE_NONE')
    }
    if (Array.isArray(value)) {
      return value.length > 0 ? value.map((item) => this.formatSettingValue(item)).join(', ') : this.translateService.instant('ACTIVITY_LOG.VALUE_NONE')
    }
    if (typeof value === 'string' && ISO_INSTANT.test(value)) {
      return this.formatDate(value)
    }
    if (typeof value === 'string' && /^[A-Z][A-Z0-9_]*$/.test(value)) {
      return humanizeToken(value)
    }
    return String(value)
  }

  private formatDate(isoDate: string | null): string {
    return isoDate === null ? '' : this.britishDatePipe.transform(isoDate, DATE_ONLY_FORMAT)
  }

  /**
   * When each action started being recorded, worded and ordered as the Action column is, so the two
   * can be read against each other.
   */
  private buildCoverageRows(actions: ActivityLogCoverage['actions']): CoverageRow[] {
    return actions
      .map((action) => ({ action: this.translateAction(action.metric), recordedSince: this.formatDate(action.recordedSince) }))
      .sort((first, second) => first.action.localeCompare(second.action))
  }

  /**
   * The actions on screen that this log cannot answer for the whole of the reader's time with us,
   * because we only started keeping them after the reader arrived.
   *
   * An empty result for one of these is not the same as the action never having happened, which is
   * the one reading an audit log must not be left to make on its own.
   */
  private buildGaps(coverage: ActivityLogCoverage | null, selectedActions: string[]): CoverageRow[] {
    if (coverage === null || coverage.customerSince === null) {
      return []
    }
    const customerSince = Date.parse(coverage.customerSince)
    // No selection is every action, which is what the table itself is showing.
    const selected = selectedActions.length === 0 ? coverage.actions : coverage.actions.filter((action) => selectedActions.includes(action.metric))
    return this.buildCoverageRows(selected.filter((action) => Date.parse(action.recordedSince) > customerSince))
  }

  /** The banner asks about particular actions, so the list it opens answers about those alone. */
  showGaps() {
    this.isCoverageOpen = true
    this.isCoverageNarrowed = true
    afterNextRender(() => this.coverageSection?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), { injector: this.injector })
  }

  toggleCoverage() {
    this.isCoverageOpen = !this.isCoverageOpen
    this.isCoverageNarrowed = false
  }
}
